/* eslint-disable camelcase */
import { Grid, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DeleteRounded, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import Datagrid from '../../Datagrid';
import styles from '../TargetModal/styles';
import columns from './table_config';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import { generateRandom } from '../../../../utils/randomNumberGenerator';
import SelectEditValueTypes from '../../SelectEditValueTypes';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { APP_ACTION_TYPES, MAX_ROW_LIMITS } from '../../../../utils/constants';
import { COLORS } from '../../../../shared/config/colors';
import { checkPermissions } from '../../../../utils/checkPermissions';

function RunsECDSpan({ units, currentUnit, runsECDSpanNames, loading, targetData, setTargetData, setParameterDirty, setTargetSaveEnable, setRunsEcdDirty, controlsTableDirty, permissions }) {
  const apiRef = useGridApiRef();
  const classes = styles();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();

  useEffect(() => {
    if (properties.length === 0 && targetData?.ecd_span) {
      const updatedData = targetData?.ecd_span?.map((item) => ({
        ...item,
        _id: generateRandom()
      }));
      setProperties(updatedData);
    }
  }, []);

  useEffect(() => {
    if (properties[0]?.name === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddECDSpan = () => {
    const nextId = properties.length;
    const newRow = {
      row: properties.length + 1,
      name: '',
      low_emw: '',
      high_emw: '',
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
    };
    setProperties((prev) => [newRow, ...prev]);
    setRunsEcdDirty(true);
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
    setRunsEcdDirty(true);
  };

  const checkRowValues = (value) => {
    const row = value;
    const invalidFields = [];
    if (row?.name === undefined || row?.name === null || row?.name === '') invalidFields.push('Name');
    if (row?.low_emw === undefined || row?.low_emw === null || row?.low_emw === '') invalidFields.push('Low EMW');
    if (row?.high_emw === undefined || row?.high_emw === null || row?.high_emw === '') invalidFields.push('High EMW');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
            `Please enter the following details: ${invalidFields.join(', ')}`,
            'error',
            new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const validateProperties = (row) => {
    const { low_emw, high_emw, _id } = row;
    if (Number(low_emw) <= units?.low_emw?.min || Number(low_emw) > units?.low_emw?.max) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          `Low EMW should be between ${units?.low_emw?.min} and ${units?.low_emw?.max}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (Number(high_emw) <= units?.high_emw?.min || Number(high_emw) > units?.high_emw?.max) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          `High EMW should be between ${units?.high_emw?.min} and ${units?.high_emw?.max}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    if (Number(high_emw) <= Number(low_emw)) {
      setInvalidRows(prev => ({ ...prev, [_id]: true }));
      dispatch(
        enqueueSnackbar(
          'High EMW should be greater than Low EMW value.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
    return true;
  };

  function checkForDuplicates(properties) {
    const seen = new Set();

    for (let i = 0; i < properties.length; i++) {
      const { name } = properties[i];
      const key = `${name}`;

      if (seen.has(key)) {
        setInvalidRows(prev => ({ ...prev, [properties[i]?._id]: true }));
        dispatch(
          enqueueSnackbar(
            'Row with name already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      }
      seen.add(key);
    }
    return true;
  }

  function getAvailableNames(tableData, dropdownOptions) {
    const selectedNames = tableData
      .map(row => row.name)
      .filter(name => !!name);

    return dropdownOptions.filter(option => !selectedNames.includes(option));
  }

  const handleSave = (row) => {
    if (checkRowValues(row)) {
      if (validateProperties(row) && checkForDuplicates(properties)) {
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        return true;
      }
    }
  };

  const markRowAsSaved = (id) => {
    let updated = [];
    setProperties((prevProperties) => {
      updated = prevProperties.map((item) => {
        if (item._id === id) {
          // eslint-disable-next-line no-unused-vars
          const { isDirty, isNewRow, ...rest } = item;
          return { ...rest };
        }
        return item;
      });
      return updated;
    });
    setTimeout(() => {
      setTargetData(prev => ({
        ...prev,
        ecd_span: updated
      }));
      setRunsEcdDirty(false);
      setTargetSaveEnable(true);
    }, 0);

    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updatedDirty = [...prev];
      updatedDirty[0] = { ...updatedDirty[0], isDirty: true };
      return updatedDirty;
    });
  };

  const removeRunsECDSpan = (row) => {
    const updated = properties.filter(item => item._id !== row?._id);
    setProperties(updated);
    const anyDirty = updated.some(item => item?.isDirty);
    if (!anyDirty) {
      setRunsEcdDirty(false);
    }
    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updatedDirty = [...prev];
      updatedDirty[0] = { ...updatedDirty[0], isDirty: true };
      return updatedDirty;
    });
    setTargetData(prev => ({
      ...prev,
      ecd_span: updated
    }));
    setTargetSaveEnable(true);
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderNameCell = (params) => {
    return <SelectEditValueTypes {...params} closeCellAfterSelect={true} updateData={updateData} data={getAvailableNames(properties, runsECDSpanNames)} />;
  };

  const renderActionCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || loading || (!row?.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, permissions))}
          onClick={() => {
            if (handleSave(row)) {
              setTimeout(() => {
                markRowAsSaved(row._id);
              }, 100);
            }
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={loading || (!row?.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, permissions))}
          onClick={() => {
            if (properties?.length === 1 && targetData?.controls_table?.length) {
              dispatch(
                enqueueSnackbar(
                  'EMW limits cannot be removed while the Controls Table has entries. Please clear the Controls Table first.',
                  'error',
                  new Date().getTime() + Math.random()
                )
              );
              return;
            }
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  return (
    <Grid container maxWidth='1150px'>
        <Grid container item marginLeft='12px' marginTop='15px' marginBottom='5px'>
          <Grid>
            <Typography variant="h5">Run&apos;s EMW Limits</Typography>
          </Grid>
        </Grid>
        <Grid container sx={{ display: !checkPermissions(APP_ACTION_TYPES.UPDATE, permissions) && 'none !important' }}>
          <Grid item marginLeft='12px' className={classes.customTool}>
            <Typography variant='button' fontWeight='700' onClick={handleAddECDSpan} className={loading || getAvailableNames(properties, runsECDSpanNames).length === 0 || properties?.some(item => item.isDirty === true) || properties?.length === MAX_ROW_LIMITS.RUNS_ECD_SPAN || controlsTableDirty ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
        <Grid item marginLeft='12px' className={classes.entryLabel}>
          <Typography variant='h6' color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Note: Maximum ${MAX_ROW_LIMITS.RUNS_ECD_SPAN} entries are allowed)`}</Typography>
        </Grid>
        </Grid>
        <Grid item xs={12} sx={{ opacity: controlsTableDirty ? 0.5 : 1, pointerEvents: controlsTableDirty && 'none' }}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
                height: '0px'
              },
            }}
            loading={loading}
            columns={columns(renderNameCell, renderValueCell, renderActionCell, units, currentUnit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            height={'22vh'}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            hideFooterPagination
            hideFooterSelectedRowCount
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={permissions}
          />
        </Grid>
          {
              deleteAction && <DeleteConfirmationModal
                  open={deleteAction}
                  handleDelete={removeRunsECDSpan}
                  handleClose={setDeleteAction}
                  deleteRow={deleteRow}
              />
          }
      </Grid>
  );
}

RunsECDSpan.propTypes = {
  units: PropTypes.object,
  currentUnit: PropTypes.string,
  runsECDSpanNames: PropTypes.array,
  loading: PropTypes.bool,
  targetData: PropTypes.object,
  setTargetData: PropTypes.func,
  setParameterDirty: PropTypes.func,
  setTargetSaveEnable: PropTypes.func,
  setRunsEcdDirty: PropTypes.func,
  controlsTableDirty: PropTypes.bool,
  permissions: PropTypes.object
};

export default RunsECDSpan;
